// Physically based sky (after Hillaire 2020, "A Scalable and Production Ready Sky and Atmosphere
// Rendering Technique"). Distances in megameters (MM) inside the atmosphere functions.

export const atmosphereGLSL = /* glsl */ `
uniform float uGroundRadiusMM;
uniform float uAtmoRadiusMM;
uniform vec3 uRayleighScat;   // per MM at sea level
uniform float uMieScat;       // per MM at sea level
uniform float uMieAbs;
uniform float uMieG;
uniform vec3 uOzoneAbs;
uniform vec3 uGroundAlbedo;
uniform float uMieScaleHeightKM;

void scatteringValues(vec3 pos, out vec3 rayleighScattering, out float mieScattering, out vec3 extinction) {
  float altitudeKM = (length(pos) - uGroundRadiusMM) * 1000.0;
  float rayleighDensity = exp(-max(altitudeKM, 0.0) / 8.0);
  float mieDensity = exp(-max(altitudeKM, 0.0) / uMieScaleHeightKM);
  rayleighScattering = uRayleighScat * rayleighDensity;
  mieScattering = uMieScat * mieDensity;
  float mieAbsorption = uMieAbs * mieDensity;
  vec3 ozoneAbsorption = uOzoneAbs * max(0.0, 1.0 - abs(altitudeKM - 25.0) / 15.0);
  extinction = rayleighScattering + mieScattering + mieAbsorption + ozoneAbsorption;
}

float safeacos(float x) { return acos(clamp(x, -1.0, 1.0)); }

float rayIntersectSphere(vec3 ro, vec3 rd, float rad) {
  float b = dot(ro, rd);
  float c = dot(ro, ro) - rad * rad;
  if (c > 0.0 && b > 0.0) return -1.0;
  float discr = b * b - c;
  if (discr < 0.0) return -1.0;
  if (discr > b * b) return (-b + sqrt(discr));
  return -b - sqrt(discr);
}

float miePhase(float cosTheta) {
  float g = uMieG;
  float scale = 3.0 / (8.0 * PI);
  float num = (1.0 - g * g) * (1.0 + cosTheta * cosTheta);
  float denom = (2.0 + g * g) * pow(max(1.0 + g * g - 2.0 * g * cosTheta, 1e-4), 1.5);
  return scale * num / denom;
}
float rayleighPhase(float cosTheta) {
  return 3.0 / (16.0 * PI) * (1.0 + cosTheta * cosTheta);
}

vec3 lutTransmittance(sampler2D lut, vec3 pos, vec3 sunDir) {
  float height = length(pos);
  vec3 up = pos / height;
  float mu = dot(sunDir, up);
  vec2 uv = vec2(clamp(0.5 + 0.5 * mu, 0.0, 1.0),
                 clamp((height - uGroundRadiusMM) / (uAtmoRadiusMM - uGroundRadiusMM), 0.0, 1.0));
  return texture(lut, uv).rgb;
}
vec3 lutMultiScatter(sampler2D lut, vec3 pos, vec3 sunDir) {
  float height = length(pos);
  vec3 up = pos / height;
  float mu = dot(sunDir, up);
  vec2 uv = vec2(clamp(0.5 + 0.5 * mu, 0.0, 1.0),
                 clamp((height - uGroundRadiusMM) / (uAtmoRadiusMM - uGroundRadiusMM), 0.0, 1.0));
  return texture(lut, uv).rgb;
}
`;

export const fullscreenVert = /* glsl */ `
out vec2 vUv;
void main() {
  vUv = position.xy * 0.5 + 0.5;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

export const transmittanceFrag = /* glsl */ `
in vec2 vUv;
out vec4 fragColor;
void main() {
  float mu = 2.0 * vUv.x - 1.0;
  float height = mix(uGroundRadiusMM, uAtmoRadiusMM, vUv.y);
  vec3 pos = vec3(0.0, height, 0.0);
  vec3 sunDir = normalize(vec3(0.0, mu, -sqrt(max(0.0, 1.0 - mu * mu))));
  vec3 trans = vec3(1.0);
  if (rayIntersectSphere(pos, sunDir, uGroundRadiusMM) > 0.0) {
    trans = vec3(0.0);
  } else {
    float atmoDist = rayIntersectSphere(pos, sunDir, uAtmoRadiusMM);
    float t = 0.0;
    const float STEPS = 40.0;
    for (float i = 0.0; i < STEPS; i += 1.0) {
      float newT = ((i + 0.3) / STEPS) * atmoDist;
      float dt = newT - t;
      t = newT;
      vec3 p = pos + t * sunDir;
      vec3 rs, ext; float ms;
      scatteringValues(p, rs, ms, ext);
      trans *= exp(-dt * ext);
    }
  }
  fragColor = vec4(trans, 1.0);
}
`;

export const multiScatterFrag = /* glsl */ `
uniform sampler2D uTransLUT;
in vec2 vUv;
out vec4 fragColor;
vec3 sphericalDir(float theta, float phi) {
  float cosPhi = cos(phi), sinPhi = sin(phi), cosTheta = cos(theta), sinTheta = sin(theta);
  return vec3(sinPhi * sinTheta, cosPhi, sinPhi * cosTheta);
}
void main() {
  float sunCosTheta = 2.0 * vUv.x - 1.0;
  float height = mix(uGroundRadiusMM, uAtmoRadiusMM, vUv.y);
  vec3 pos = vec3(0.0, height, 0.0);
  vec3 sunDir = normalize(vec3(0.0, sunCosTheta, -sqrt(max(0.0, 1.0 - sunCosTheta * sunCosTheta))));
  vec3 lumTotal = vec3(0.0), fms = vec3(0.0);
  const int SQRT = 8;
  const float STEPS = 20.0;
  float invSamples = 1.0 / float(SQRT * SQRT);
  for (int i = 0; i < SQRT; i++) {
    for (int j = 0; j < SQRT; j++) {
      float theta = PI * (float(i) + 0.5) / float(SQRT);
      float phi = safeacos(1.0 - 2.0 * (float(j) + 0.5) / float(SQRT));
      vec3 rayDir = sphericalDir(theta, phi);
      float atmoDist = rayIntersectSphere(pos, rayDir, uAtmoRadiusMM);
      float groundDist = rayIntersectSphere(pos, rayDir, uGroundRadiusMM);
      float tMax = groundDist > 0.0 ? groundDist : atmoDist;
      float cosTheta = dot(rayDir, sunDir);
      float mieP = miePhase(cosTheta);
      float rayP = rayleighPhase(-cosTheta);
      vec3 lum = vec3(0.0), lumFactor = vec3(0.0), trans = vec3(1.0);
      float t = 0.0;
      for (float s = 0.0; s < STEPS; s += 1.0) {
        float newT = ((s + 0.3) / STEPS) * tMax;
        float dt = newT - t;
        t = newT;
        vec3 p = pos + t * rayDir;
        vec3 rs, ext; float ms;
        scatteringValues(p, rs, ms, ext);
        vec3 sampleTrans = exp(-dt * ext);
        vec3 scatNoPhase = rs + ms;
        vec3 scatF = (scatNoPhase - scatNoPhase * sampleTrans) / ext;
        lumFactor += trans * scatF;
        vec3 sunTrans = lutTransmittance(uTransLUT, p, sunDir);
        vec3 inScat = (rs * rayP + ms * mieP) * sunTrans;
        vec3 scatInt = (inScat - inScat * sampleTrans) / ext;
        lum += scatInt * trans;
        trans *= sampleTrans;
      }
      if (groundDist > 0.0) {
        vec3 hitPos = pos + groundDist * rayDir;
        if (dot(pos, sunDir) > 0.0) {
          hitPos = normalize(hitPos) * uGroundRadiusMM;
          lum += trans * uGroundAlbedo * lutTransmittance(uTransLUT, hitPos, sunDir);
        }
      }
      fms += lumFactor * invSamples;
      lumTotal += lum * invSamples;
    }
  }
  vec3 psi = lumTotal / (1.0 - fms);
  fragColor = vec4(psi, 1.0);
}
`;

// Sky-view LUT in absolute azimuth (u: 0..1 -> azimuth 0..2PI measured from -Z toward +X),
// v: non-linear altitude mapping. Contains sun + moon single/multiple scattering (relative units).
export const skyViewFrag = /* glsl */ `
uniform sampler2D uTransLUT;
uniform sampler2D uMSLUT;
uniform vec3 uViewPosMM;
uniform vec3 uSunDirW;
uniform vec3 uSunE;
uniform vec3 uMoonDirW;
uniform vec3 uMoonE;
in vec2 vUv;
out vec4 fragColor;

vec3 scatterLight(vec3 pos, vec3 rayDir, vec3 lightDir, float tMax) {
  float cosTheta = dot(rayDir, lightDir);
  float mieP = miePhase(cosTheta);
  float rayP = rayleighPhase(-cosTheta);
  vec3 lum = vec3(0.0), trans = vec3(1.0);
  float t = 0.0;
  const float STEPS = 30.0;
  for (float i = 0.0; i < STEPS; i += 1.0) {
    float newT = ((i + 0.3) / STEPS) * tMax;
    float dt = newT - t;
    t = newT;
    vec3 p = pos + t * rayDir;
    vec3 rs, ext; float ms;
    scatteringValues(p, rs, ms, ext);
    vec3 sampleTrans = exp(-dt * ext);
    vec3 sunTrans = lutTransmittance(uTransLUT, p, lightDir);
    vec3 psiMS = lutMultiScatter(uMSLUT, p, lightDir);
    vec3 inScat = rs * (rayP * sunTrans + psiMS) + ms * (mieP * sunTrans + psiMS);
    vec3 scatInt = (inScat - inScat * sampleTrans) / ext;
    lum += scatInt * trans;
    trans *= sampleTrans;
  }
  return lum;
}

void main() {
  float azimuth = vUv.x * 2.0 * PI;
  float v = vUv.y;
  float adjV = v < 0.5 ? -(1.0 - 2.0 * v) * (1.0 - 2.0 * v) : (2.0 * v - 1.0) * (2.0 * v - 1.0);
  float height = length(uViewPosMM);
  float horizonAngle = safeacos(sqrt(max(height * height - uGroundRadiusMM * uGroundRadiusMM, 0.0)) / height) - 0.5 * PI;
  float altitudeAngle = adjV * 0.5 * PI - horizonAngle;
  float ca = cos(altitudeAngle);
  vec3 rayDir = vec3(ca * sin(azimuth), sin(altitudeAngle), -ca * cos(azimuth));
  float atmoDist = rayIntersectSphere(uViewPosMM, rayDir, uAtmoRadiusMM);
  float groundDist = rayIntersectSphere(uViewPosMM, rayDir, uGroundRadiusMM);
  float tMax = groundDist < 0.0 ? atmoDist : groundDist;
  vec3 lum = scatterLight(uViewPosMM, rayDir, uSunDirW, tMax) * uSunE;
  if (dot(uMoonE, vec3(1.0)) > 0.0) lum += scatterLight(uViewPosMM, rayDir, uMoonDirW, tMax) * uMoonE;
  fragColor = vec4(lum, 1.0);
}
`;

// Sample helper for the sky-view LUT (absolute azimuth parameterization).
export const skyViewSampleGLSL = /* glsl */ `
uniform sampler2D uSkyViewLUT;
uniform vec3 uViewPosMM;
vec3 sampleSkyView(vec3 rayDir) {
  float height = length(uViewPosMM);
  float horizonAngle = safeacos(sqrt(max(height * height - uGroundRadiusMM * uGroundRadiusMM, 0.0)) / height);
  float altitudeAngle = horizonAngle - acos(clamp(rayDir.y, -1.0, 1.0));
  float azimuth = atan(rayDir.x, -rayDir.z);
  if (azimuth < 0.0) azimuth += 2.0 * PI;
  float v = 0.5 + 0.5 * sign(altitudeAngle) * sqrt(abs(altitudeAngle) * 2.0 / PI);
  return texture(uSkyViewLUT, vec2(azimuth / (2.0 * PI), v)).rgb;
}
`;

// Integrates the sky-view LUT into SH9 irradiance + misc lighting values (16x1 target).
export const skyIrrFrag = /* glsl */ `
uniform sampler2D uTransLUT;
uniform vec3 uSunDirW;
uniform vec3 uSunE;
uniform vec3 uMoonDirW;
uniform vec3 uMoonE;
uniform float uCloudCover;
uniform float uCloudDarkness;
uniform float uCamAltM;
uniform vec3 uGroundAlbedoLocal;
out vec4 fragColor;

float shBasis(int i, vec3 n) {
  float x = n.x, y = n.y, z = n.z;
  if (i == 0) return 0.282095;
  if (i == 1) return 0.488603 * y;
  if (i == 2) return 0.488603 * z;
  if (i == 3) return 0.488603 * x;
  if (i == 4) return 1.092548 * x * y;
  if (i == 5) return 1.092548 * y * z;
  if (i == 6) return 0.315392 * (3.0 * z * z - 1.0);
  if (i == 7) return 1.092548 * x * z;
  return 0.546274 * (x * x - y * y);
}
vec3 transAt(vec3 dir) {
  vec3 pos = vec3(0.0, uGroundRadiusMM + uCamAltM * 1e-6, 0.0);
  float hz = rayIntersectSphere(pos, dir, uGroundRadiusMM);
  return hz > 0.0 ? vec3(0.0) : lutTransmittance(uTransLUT, pos, dir);
}
// overcast sky radiance: diffuse light through the cloud deck
vec3 cloudRadiance(vec3 dir) {
  vec3 sunT = transAt(uSunDirW) * uSunE + transAt(uMoonDirW) * uMoonE;
  float sunUp = max(uSunDirW.y, 0.0);
  float l = (0.012 + 0.05 * sunUp) * (1.0 - 0.75 * uCloudDarkness);
  return vec3(l * 1.02, l * 1.03, l * 1.08) * (sunT.r + sunT.g + sunT.b) / 3.0 * (0.6 + 0.4 * dir.y);
}
void main() {
  int idx = int(gl_FragCoord.x);
  vec3 acc = vec3(0.0);
  const int NT = 16, NP = 32;
  if (idx <= 8) {
    for (int i = 0; i < NT; i++) {
      float ct = (float(i) + 0.5) / float(NT); // cos(theta) over upper hemisphere
      float st = sqrt(1.0 - ct * ct);
      for (int j = 0; j < NP; j++) {
        float ph = 2.0 * PI * (float(j) + 0.5) / float(NP);
        vec3 d = vec3(st * sin(ph), ct, -st * cos(ph));
        vec3 L = sampleSkyView(d);
        L = mix(L, cloudRadiance(d), uCloudCover);
        acc += L * shBasis(idx, vec3(d.x, d.y, d.z));
      }
    }
    // lower hemisphere: hazy horizon continuation (small)
    acc *= 2.0 * PI / float(NT * NP);
    // cosine-lobe convolution (Ramamoorthi & Hanrahan), divided by PI for irradiance/PI
    float A = idx == 0 ? 3.141593 : (idx <= 3 ? 2.094395 : 0.785398);
    acc = acc * A / PI;
    // sh basis evaluation in lighting is done with axes (x,y,z) -> we stored with (x,y,z) directly
  } else if (idx == 9) {
    acc = transAt(uSunDirW);
  } else if (idx == 10) {
    // irradiance bounced from sunlit ground/rock around the viewer (per unit albedo)
    vec3 Esun = transAt(uSunDirW) * uSunE * max(uSunDirW.y, 0.0) + transAt(uMoonDirW) * uMoonE * max(uMoonDirW.y, 0.0);
    vec3 EskyUp = vec3(0.0);
    for (int j = 0; j < NP; j++) {
      float ph = 2.0 * PI * (float(j) + 0.5) / float(NP);
      vec3 d = normalize(vec3(sin(ph) * 0.6, 0.8, -cos(ph) * 0.6));
      EskyUp += mix(sampleSkyView(d), cloudRadiance(d), uCloudCover);
    }
    EskyUp *= PI / float(NP);
    acc = (Esun * (1.0 - 0.85 * uCloudCover) + EskyUp) * 0.5;
  } else if (idx == 11) {
    // airglow + starlight floor (keeps moonless nights from being pitch black)
    acc = vec3(0.0009, 0.0011, 0.0016) * 0.6;
  } else if (idx == 12) {
    vec3 s = vec3(0.0);
    for (int j = 0; j < NP; j++) {
      float ph = 2.0 * PI * (float(j) + 0.5) / float(NP);
      s += sampleSkyView(normalize(vec3(sin(ph) * 0.7, 0.7, -cos(ph) * 0.7)));
    }
    acc = s / float(NP);
  }
  fragColor = vec4(acc, 1.0);
}
`;
